import type { CheckoutAppearance } from './core.types';

// The widget's outline, drawn before the app arrives: the same card, image
// block, message block and reward bar the app paints, in the same colours and
// radius, so the handoff only changes the text. Each text bar is tinted from
// the surface it sits on (--c), with the primary, or white when dark (--k).
// Only the text bars breathe, in
// a top-to-bottom wave, on opacity alone so the compositor runs it while the
// page is busy. The heights are the carbon widget's (344px wide, 506px below
// the app's 448px container breakpoint).
// The merchant's box wins: the host fills a fixed height, takes the
// container's max-height, and scrolls the widget vertically when it is taller.
// The slot never shrinks below the app's height but grows into a taller fixed
// height, as a flex column the app can fill. The skeleton's footer bar (.ft)
// and the app's footer row both take margin-top:auto, so they sit at the
// bottom of the card.
// While the skeleton is in flow the app's slot is laid out but invisible.
// reveal() fades the slot in over it and eases the height to the slot's.
// rest() is how every failure ends: the skeleton, or the live widget, settles
// into the ekko card (.rs), a slim one-line banner in the same place, so
// nothing that was on the page disappears. The banner keeps its own height and
// does not fill a fixed box. The mark's ring is a CSS gradient behind the SVG,
// because an SVG gradient's url(#id) is unreliable inside a shadow root.
// The skeleton and the card share the colours, the merchant's when given:
// --p primary, --s secondary, --r radius, --c surface, --k text when dark.
// They are set on both inside the shadow root, not on the host, so the
// merchant's page can neither override them nor leak its own (--k is reset
// for the light theme, where the text falls back to --p).
const EASE_OUT = 'cubic-bezier(.23,1,.32,1)';
const EASE_IN_OUT = 'cubic-bezier(.77,0,.175,1)';
const CSS_TEXT =
  ':host{display:flex;flex-direction:column;position:relative;width:100%;height:100%;max-height:inherit;overflow-y:auto}' +
  '.sk,.rs{--p:#1A4432;--s:#EDF0EF;--r:20px;--c:#fff;--k:initial}' +
  ':host([theme=dark]) :is(.sk,.rs){--c:#000;--k:#fff}' +
  '.slot{position:relative;display:flex;flex-direction:column;flex:1 0 auto}' +
  '.sk:not(.out)+.slot{position:absolute;top:0;left:0;width:100%;visibility:hidden}' +
  '.out{position:absolute;top:0;left:0;width:100%}' +
  '.sk{container-type:inline-size;display:flex;flex-direction:column;flex:auto;box-sizing:border-box;min-height:0;overflow:hidden;' +
  `border:1px solid var(--p);border-radius:var(--r);background:color-mix(in srgb,var(--p) 8%,var(--c));animation:f .2s ${EASE_OUT}}` +
  '.cd{flex:1;display:flex;gap:4px;padding:4px;border-radius:var(--r);background:var(--c)}' +
  '.im{width:120px;border-radius:var(--r);background:#D8E0DD}' +
  '.ct{flex:1;display:flex;flex-direction:column;gap:24px;padding-bottom:12px}' +
  '.ms{--c:var(--s);--k:var(--p);height:98px;border-radius:var(--r);background:var(--s)}' +
  ':where(.sk i){--b:color-mix(in srgb,var(--k,var(--p)) 13%,var(--c));display:flex;justify-content:space-between;align-items:center;padding:0 16px;animation:b 1.8s ease-in-out infinite}' +
  ':where(.sk i):before,:where(.sk i):after{content:"";height:10px;border-radius:5px;background:var(--b)}' +
  '.tx{height:100%;flex-direction:column;justify-content:center;gap:12px}.tx:before{width:45%;height:12px}.tx:after{width:70%}' +
  '.rw{height:44px}.rw:before{width:45%}.rw:after{width:36px;height:20px;border-radius:10px}' +
  '.ft{height:24px;margin-top:auto}.ft:before{width:80px}.ft:after{width:110px}' +
  '.bn{height:40px;justify-content:center}.bn:after{display:none}.bn:before{width:60%}' +
  '.rw{animation-delay:.09s}.rw+.rw{animation-delay:.18s}.ft{animation-delay:.27s}.bn{animation-delay:.36s}' +
  '.rs{flex:none;display:flex;align-items:center;gap:10px;box-sizing:border-box;min-height:48px;padding:8px 20px 8px 10px;border-radius:99px;' +
  'background:color-mix(in srgb,var(--p) 8%,var(--c));font:600 13px/1.4 system-ui,sans-serif;color:var(--k,var(--p));' +
  `animation:f .2s ${EASE_OUT}}` +
  '.mk{flex:none;width:28px;height:28px;border-radius:50%;background:linear-gradient(105deg,#50FF78,#009BE7)}' +
  '.mk svg{display:block;width:100%;height:100%}' +
  '@container (max-width:447.9px){.cd{flex-direction:column}.im{width:auto;height:120px}.ms{height:116px}.bn{height:60px}}' +
  '@keyframes f{from{opacity:0}}@keyframes b{50%{opacity:.45}}' +
  '@media(prefers-reduced-motion:reduce){:where(.sk i){animation:none}}';

let sheet: CSSStyleSheet | undefined;

function styles(): CSSStyleSheet {
  if (!sheet) {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(CSS_TEXT);
  }
  return sheet;
}

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

const SVG = 'http://www.w3.org/2000/svg';
// The ekko mark inside its ring (viewBox 40), rounded to 0.1.
const MARK: [string, string][] = [
  ['M20 4.7a15.3 15.3 0 1 1 0 30.6a15.3 15.3 0 1 1 0-30.6Z', '#009BE7'],
  ['M20 8a12 12 0 1 1 0 24a12 12 0 1 1 0-24Z', '#1B1E64'],
  [
    'M27.6 21.7H29.4C29.4 21.6 29.4 21 29.4 20C29.4 14.7 25.4 10.7 20 10.7C14.6 10.7 10.6 14.7 10.6 20' +
      'C10.6 25.3 14.6 29.3 20 29.3C23.3 29.3 26 27.8 27.7 25.5L23.3 23.4C22.5 24.3 21.3 24.9 20 24.9' +
      'C18 24.9 16.3 23.6 15.6 21.7H27.6ZM15.6 18.3C16.3 16.4 18 15.1 20 15.1C22 15.1 23.7 16.4 24.4 18.3H15.6Z',
    '#fff',
  ],
];
const REST_TEXT = "Sorry, your footprint didn't load this time.";

export interface WidgetElement {
  element: HTMLElement;
  root: ShadowRoot;
  slot: HTMLElement;
  /** Fades the slot in over the skeleton, easing the height to the slot's. */
  reveal(): void;
  /** Settles the skeleton or the widget into the ekko card, easing the height to it. */
  rest(): void;
}

export function createWidget(
  container: HTMLElement,
  appearance: Partial<CheckoutAppearance> = {},
  rewardBar = true,
): WidgetElement {
  const element = document.createElement('ekko-checkout');
  if (appearance.theme) element.setAttribute('theme', appearance.theme);
  // A 3.x UMD on the same page defines <ekko-checkout> with its own open root.
  const root = element.shadowRoot || element.attachShadow({ mode: 'open' });
  // The merchant's colours, through the CSSOM: never parsed as CSS text, and
  // no style attribute.
  const own = new CSSStyleSheet();
  own.insertRule('.sk,.rs{}');
  const custom = (own.cssRules[0] as CSSStyleRule).style;
  const { primaryColor, secondaryColor, outerBorderRadius } = appearance;
  if (primaryColor) custom.setProperty('--p', primaryColor);
  if (secondaryColor) custom.setProperty('--s', secondaryColor);
  if (outerBorderRadius !== undefined) custom.setProperty('--r', `${outerBorderRadius}px`);
  root.adoptedStyleSheets = [styles(), own];

  const add = (parent: ParentNode, className: string, tag = 'div') => {
    const node = document.createElement(tag);
    node.className = className;
    parent.append(node);
    return node;
  };
  const skeleton = add(root, 'sk');
  skeleton.setAttribute('aria-busy', 'true');
  skeleton.setAttribute('aria-label', 'Loading');
  const card = add(skeleton, 'cd');
  add(card, 'im');
  const content = add(card, 'ct');
  add(add(content, 'ms'), 'tx', 'i');
  add(content, 'rw', 'i');
  add(content, 'rw', 'i');
  add(content, 'ft', 'i');
  if (rewardBar) add(skeleton, 'bn', 'i');
  const slot = add(root, 'slot');
  container.appendChild(element);

  return {
    element,
    root,
    slot,
    reveal() {
      const from = element.offsetHeight;
      skeleton.classList.add('out');
      const to = element.offsetHeight;
      const fade = slot.animate?.({ opacity: [0, 1] }, { duration: 250, easing: EASE_OUT });
      if (!fade) return skeleton.remove();
      fade.onfinish = () => skeleton.remove();
      if (from !== to && !reducedMotion()) {
        element.animate(
          { height: [`${from}px`, `${to}px`], overflow: ['hidden', 'hidden'] },
          { duration: 300, easing: EASE_IN_OUT },
        );
      }
    },
    rest() {
      const from = element.offsetHeight;
      // The app is unmounted from the slot after this; it no longer shows.
      skeleton.remove();
      slot.remove();
      const card = add(root, 'rs');
      const ring = add(card, 'mk', 'span');
      const svg = document.createElementNS(SVG, 'svg');
      svg.setAttribute('viewBox', '0 0 40 40');
      svg.setAttribute('aria-hidden', 'true');
      for (const [d, fill] of MARK) {
        const path = document.createElementNS(SVG, 'path');
        path.setAttribute('d', d);
        path.setAttribute('fill', fill);
        svg.append(path);
      }
      ring.append(svg);
      card.append(REST_TEXT);
      const to = element.offsetHeight;
      if (from !== to && !reducedMotion()) {
        element.animate?.(
          { height: [`${from}px`, `${to}px`], overflow: ['hidden', 'hidden'] },
          { duration: 300, easing: EASE_IN_OUT },
        );
      }
    },
  };
}
